import { useState } from "react";

import type { EquipmentAvailability } from "@eisox/api/core-api";
import { Typography } from "@eisox/design-system";
import { GaugeIcon, PastIcon, RefreshIcon, WarningIcon } from "@eisox/icons";

import { KpiGrid } from "~/components/kpi-grid";
import { m } from "~/paraglide/messages";

import { formatAvailability, formatDuration } from "../helpers/connectivity-history.helper";
import { ConnectivityDayEvents } from "./connectivity-day-events";
import { ConnectivityDaysChart } from "./connectivity-days-chart";

interface ConnectivityAvailabilityProps {
  availability: EquipmentAvailability;
  timezone: string;
}

const ConnectivityAvailability: React.FC<ConnectivityAvailabilityProps> = ({
  availability: { summary, days },
  timezone,
}) => {
  const [selectedDayStart, setSelectedDayStart] = useState<string>();
  const observedDays = days.filter(({ segments }) => segments.length > 0);
  const selectedDay = days.find(({ start }) => start === selectedDayStart) ?? observedDays.at(-1);

  return (
    <div className="gap-8 min-h-0 flex flex-1 flex-col overflow-y-auto">
      <KpiGrid
        kpis={[
          {
            Icon: GaugeIcon,
            value: formatAvailability(summary.availability),
            label: m.connectivity_history_availability(),
            help: m.connectivity_history_availability_help(),
          },
          {
            Icon: PastIcon,
            value: formatDuration(summary.disconnectedMs),
            label: m.connectivity_history_offline_time(),
            color: summary.disconnectedMs > 0 ? "red" : undefined,
          },
          {
            Icon: WarningIcon,
            value: summary.missedPings.toLocaleString(),
            label: m.connectivity_history_missed_pings(),
            color: summary.missedPings > 0 ? "orange" : undefined,
          },
          {
            Icon: RefreshIcon,
            value: summary.earlyPings.toLocaleString(),
            label: m.connectivity_history_reboots(),
            help: m.connectivity_history_reboots_help(),
            color: summary.earlyPings > 0 ? "darkBlue" : undefined,
          },
        ]}
      />
      <section className="gap-2.5 flex flex-col">
        <div>
          <Typography as="h3" className="font-semibold text-lg">
            {m.connectivity_history_availability()}
          </Typography>
          <Typography className="text-sm text-primary-dark-gray">{m.connectivity_history_select_day_hint()}</Typography>
        </div>
        <ConnectivityDaysChart
          days={days}
          selectedDayStart={selectedDay?.start}
          onSelectDay={setSelectedDayStart}
          timezone={timezone}
        />
      </section>
      {selectedDay && <ConnectivityDayEvents day={selectedDay} timezone={timezone} />}
    </div>
  );
};

ConnectivityAvailability.displayName = "ConnectivityAvailability";

export { ConnectivityAvailability };
